import { useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Crown, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fetchConfig, WHOP_LOGIN_URL } from "@/lib/api";
import Logo from "@/components/Logo";

const ERRORS = {
  whop_denied: "Whop login was cancelled.",
  invalid_state: "Login session expired — please try again.",
  whop_failed: "We couldn't verify your Whop account. Please try again.",
};

export default function Login() {
  const { isMember, loginEnabled, checking } = useAuth();
  const [params] = useSearchParams();
  const { data: config } = useQuery({ queryKey: ["config"], queryFn: fetchConfig });
  const vipUrl = config?.whop?.vip_checkout_url || "/#pricing";

  useEffect(() => {
    const err = params.get("error");
    if (err) toast.error(ERRORS[err] || "Login failed");
  }, [params]);

  if (!checking && isMember) return <Navigate to="/dashboard" replace />;

  return (
    <div className="mx-auto flex max-w-7xl items-center justify-center px-4 py-20 sm:px-6 lg:px-8" data-testid="login-page">
      <div className="panel w-full max-w-md p-8">
        <div className="flex justify-center"><Logo compact /></div>
        <h1 className="mt-6 text-center font-display text-3xl font-extrabold uppercase tracking-tight text-white">
          Member Login
        </h1>
        <p className="mt-3 text-center text-sm leading-relaxed text-slate-400">
          Sign in with the Whop account you used at checkout. We verify your VIP membership server-side and unlock your dashboard.
        </p>

        <div className="mt-8 space-y-3">
          {loginEnabled ? (
            <a
              href={WHOP_LOGIN_URL}
              target="_top"
              data-testid="login-whop-btn"
              className="btn-primary w-full"
              onClick={(e) => {
                if (window.self !== window.top) {
                  e.preventDefault();
                  window.open(WHOP_LOGIN_URL, "_blank", "noopener");
                }
              }}
            >
              <LogIn size={16} /> Login With Whop
            </a>
          ) : (
            <button disabled data-testid="login-whop-btn" className="btn-primary w-full cursor-not-allowed opacity-60" title="Whop login is not configured yet">
              <LogIn size={16} /> Login With Whop
            </button>
          )}
          <a href={vipUrl} target={vipUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="login-join-vip-btn" className="btn-outline w-full">
            <Crown size={16} /> Not a member? Join VIP
          </a>
        </div>

        {!loginEnabled && (
          <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-wider text-[#F59E0B]/80" data-testid="login-not-configured-note">
            Member login activates once Whop credentials are connected
          </p>
        )}
        <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-wider text-slate-600">
          Free picks never require an account · Membership & payments handled securely by Whop
        </p>
      </div>
    </div>
  );
}
